import React, { useEffect, useState } from 'react';
import { app, db } from '../Admin/Config/Config';
import { collection, doc, getCountFromServer, getDoc, limit, onSnapshot, orderBy, query, setDoc, startAfter, updateDoc, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import IncomingCall from './IncommingCall';
import Instruction from './Instruction';
import Callui from './Callui';
import Toast from '../Toast/Toast';
import Plan from './Plan';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUser } from '../Auth/UsersContext';
import LoadingTwo from '../Loading/LoadingTwo';
import { usePaymentSuccess } from '../Payment/PaymentStatusContext';

export default function Audio() {
  const { user } = useUser();
  const [userId, setUserId] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [usageQuata, setUsageQuata] = useState(0);
  const [payment, setPayment] = useState("");
  const { openPaymentSuccess } = usePaymentSuccess();

  // ============ Audio call status ============ //
  const updateStatus = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const uid = user.uid;
    await setDoc(
      doc(db, "bristu_talk_users", uid),
      {
        availability: !isAvailable ? "online" : "offline",
        last_active: new Date(),
      },
      { merge: true }
    );
    setIsAvailable(!isAvailable);
  };

  useEffect(() => {
    const auth = getAuth();
    let uid;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      uid = user.uid;
      setUserId(uid);
      const name = user.displayName ?? "Anonymous";
      const userRef = doc(db, "bristu_talk_users", uid);
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        await setDoc(
          userRef,
          {
            availability: "online",
            last_active: new Date(),
            level: "Beginner",
          },
          { merge: true }
        );
      } else {
        await setDoc(
          userRef,
          {
            availability: "online",
            avatar: "",
            name: name,
            current_plan_usage_left: 600,
            last_active: new Date(),
            level: "Beginner",
            payment: "trial",
            uid: uid,
            email: user?.email ?? "",
            sender_call: 0,
            received_call: 0,
            createdAt: new Date(),
          },
          { merge: true }
        );
      }
      setIsAvailable(true);

      // ✅ Handle user going offline when app is closed
    
    });
    return () => unsubscribe();
  }, []);


  // =============================================================== //
  //           STATUS CHANGE ONLINE OFFLINE (DON"T TOUCH)            //
  // =============================================================== //
useEffect(() => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;

  const uid = user.uid;
  const talkRef = doc(db, "bristu_talk_users", uid);
  const handleVisibilityChange = async () => {
    if (document.visibilityState === "hidden") {
        try {
        await updateDoc(talkRef, {
          availability: "offline",
          last_active: new Date(),
        });
      } catch (err) {
        console.error("Failed to update offline status:", err);
      }
    } else if (document.visibilityState === "visible") {
      try {
        await updateDoc(talkRef, {
          availability: "online",
          last_active: new Date(),
        });
      } catch (err) {
        console.error("Failed to update online status:", err);
      }
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, []);



  // =================== PAGINATION STATE =================== //
  const PAGE_SIZE = 50;
  const [beginnerUsers, setBeginnerUsers] = useState([]);
  const [intermediateUsers, setIntermediateUsers] = useState([]);
  const [advancedUsers, setAdvancedUsers] = useState([]);

  const [beginnerLast, setBeginnerLast] = useState(null);
  const [intermediateLast, setIntermediateLast] = useState(null);
  const [advancedLast, setAdvancedLast] = useState(null);

  const [loadingBeginner, setLoadingBeginner] = useState(false);
  const [loadingIntermediate, setLoadingIntermediate] = useState(false);
  const [loadingAdvanced, setLoadingAdvanced] = useState(false);
  const [advancedCount, setAdvancedCount] = useState(0);
  const [intermediateCount, setIntermediateCount] = useState(0);
  const [beginnerCount, setBeginnerCount] = useState(0);

  useEffect(() => {
    CountFetch();
  }, []);

  const CountFetch = async () => {
    const usersRef = collection(db, "bristu_talk_users");
    const beginnerQuery = query(usersRef, where("level", "==", "Beginner"));
    const intermediateQuery = query(usersRef, where("level", "==", "Intermediate"));
    const advancedQuery = query(usersRef, where("level", "==", "Advanced"));
    const beginnerSnapshot = await getCountFromServer(beginnerQuery);
    const intermediateSnapshot = await getCountFromServer(intermediateQuery);
    const advancedSnapshot = await getCountFromServer(advancedQuery);
    setBeginnerCount(beginnerSnapshot.data().count);
    setIntermediateCount(intermediateSnapshot.data().count);
    setAdvancedCount(advancedSnapshot.data().count);
  };

  useEffect(() => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const userDocRef = doc(db, "bristu_talk_users", currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsAvailable(data.availability === "online");
        setUsageQuata(data.current_plan_usage_left);
        setPayment(data.payment);
      }
    });
    return () => unsubscribe();
  }, []);

  // =================== USERS DATA =================== //
  useEffect(() => {
    const usersRef = collection(db, "bristu_talk_users");

    // Beginner: fetch all online, then paginated rest
    const onlineBeginnerQuery = query(
      usersRef,
      where("level", "==", "Beginner"),
      where("availability", "==", "online"),
      orderBy("last_active", "desc")
    );
    const beginnerQuery = query(
      usersRef,
      where("level", "==", "Beginner"),
      orderBy("last_active", "desc"),
      limit(PAGE_SIZE)
    );

    // Intermediate
    const onlineIntermediateQuery = query(
      usersRef,
      where("level", "==", "Intermediate"),
      where("availability", "==", "online"),
      orderBy("last_active", "desc")
    );
    const intermediateQuery = query(
      usersRef,
      where("level", "==", "Intermediate"),
      orderBy("last_active", "desc"),
      limit(PAGE_SIZE)
    );

    // Advanced
    const onlineAdvancedQuery = query(
      usersRef,
      where("level", "==", "Advanced"),
      where("availability", "==", "online"),
      orderBy("last_active", "desc")
    );
    const advancedQuery = query(
      usersRef,
      where("level", "==", "Advanced"),
      orderBy("last_active", "desc"),
      limit(PAGE_SIZE)
    );

    // Beginner users
    const unsubscribeBeginnerOnline = onSnapshot(onlineBeginnerQuery, (snapshot) => {
      const onlineUsers = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid) {
          onlineUsers.push(docData);
        }
      });
      setBeginnerUsers((prev) => {
        // Remove previous online users, keep paginated offline/busy
        const offlineBusy = prev.filter(u => u.availability !== "online");
        return [...onlineUsers, ...offlineBusy];
      });
    });
    const unsubscribeBeginner = onSnapshot(beginnerQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid && docData.availability !== "online") {
          users.push(docData);
        }
      });
      setBeginnerUsers((prev) => {
        // Keep online users from online query, add paginated offline/busy
        const online = prev.filter(u => u.availability === "online");
        return [...online, ...users];
      });
      setBeginnerLast(snapshot.docs[snapshot.docs.length - 1]);
    });

    // Intermediate users
    const unsubscribeIntermediateOnline = onSnapshot(onlineIntermediateQuery, (snapshot) => {
      const onlineUsers = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid) {
          onlineUsers.push(docData);
        }
      });
      setIntermediateUsers((prev) => {
        const offlineBusy = prev.filter(u => u.availability !== "online");
        return [...onlineUsers, ...offlineBusy];
      });
    });
    const unsubscribeIntermediate = onSnapshot(intermediateQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid && docData.availability !== "online") {
          users.push(docData);
        }
      });
      setIntermediateUsers((prev) => {
        const online = prev.filter(u => u.availability === "online");
        return [...online, ...users];
      });
      setIntermediateLast(snapshot.docs[snapshot.docs.length - 1]);
    });

    // Advanced users
    const unsubscribeAdvancedOnline = onSnapshot(onlineAdvancedQuery, (snapshot) => {
      const onlineUsers = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid) {
          onlineUsers.push(docData);
        }
      });
      setAdvancedUsers((prev) => {
        const offlineBusy = prev.filter(u => u.availability !== "online");
        return [...onlineUsers, ...offlineBusy];
      });
    });
    const unsubscribeAdvanced = onSnapshot(advancedQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid && docData.availability !== "online") {
          users.push(docData);
        }
      });
      setAdvancedUsers((prev) => {
        const online = prev.filter(u => u.availability === "online");
        return [...online, ...users];
      });
      setAdvancedLast(snapshot.docs[snapshot.docs.length - 1]);
    });

    return () => {
      unsubscribeBeginnerOnline();
      unsubscribeBeginner();
      unsubscribeIntermediateOnline();
      unsubscribeIntermediate();
      unsubscribeAdvancedOnline();
      unsubscribeAdvanced();
    };
  }, []);

  // Load more functions for each level (only loads offline/busy users)
  const loadMoreBeginner = () => {
    if (!beginnerLast) return;
    setLoadingBeginner(true);
    const usersRef = collection(db, "bristu_talk_users");
    const nextQuery = query(
      usersRef,
      where("level", "==", "Beginner"),
      orderBy("last_active", "desc"),
      startAfter(beginnerLast),
      limit(PAGE_SIZE)
    );
    onSnapshot(nextQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid && docData.availability !== "online") {
          users.push(docData);
        }
      });
      setBeginnerUsers((prev) => [...prev, ...users]);
      setBeginnerLast(snapshot.docs[snapshot.docs.length - 1]);
      setLoadingBeginner(false);
    });
  };

  const loadMoreIntermediate = () => {
    if (!intermediateLast) return;
    setLoadingIntermediate(true);
    const usersRef = collection(db, "bristu_talk_users");
    const nextQuery = query(
      usersRef,
      where("level", "==", "Intermediate"),
      orderBy("last_active", "desc"),
      startAfter(intermediateLast),
      limit(PAGE_SIZE)
    );
    onSnapshot(nextQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid && docData.availability !== "online") {
          users.push(docData);
        }
      });
      setIntermediateUsers((prev) => [...prev, ...users]);
      setIntermediateLast(snapshot.docs[snapshot.docs.length - 1]);
      setLoadingIntermediate(false);
    });
  };

  const loadMoreAdvanced = () => {
    if (!advancedLast) return;
    setLoadingAdvanced(true);
    const usersRef = collection(db, "bristu_talk_users");
    const nextQuery = query(
      usersRef,
      where("level", "==", "Advanced"),
      orderBy("last_active", "desc"),
      startAfter(advancedLast),
      limit(PAGE_SIZE)
    );
    onSnapshot(nextQuery, (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        const docData = { id: doc.id, ...doc.data() };
        if (docData.uid !== getAuth().currentUser?.uid && docData.availability !== "online") {
          users.push(docData);
        }
      });
      setAdvancedUsers((prev) => [...prev, ...users]);
      setAdvancedLast(snapshot.docs[snapshot.docs.length - 1]);
      setLoadingAdvanced(false);
    });
  };

  const statusColors = {
    online: "bg-green-500",
    busy: "bg-orange-500",
    offline: "bg-gray-400",
  };

  const levelCategories = [
    {
      title: "Beginner",
      students: beginnerUsers,
      color: "bg-blue-100 text-blue-800",
      loadMore: loadMoreBeginner,
      loading: loadingBeginner,
      lastVisible: beginnerLast,
    },
    {
      title: "Intermediate",
      students: intermediateUsers,
      color: "bg-green-100 text-green-800",
      loadMore: loadMoreIntermediate,
      loading: loadingIntermediate,
      lastVisible: intermediateLast,
    },
    {
      title: "Advanced",
      students: advancedUsers,
      color: "bg-red-100 text-red-800",
      loadMore: loadMoreAdvanced,
      loading: loadingAdvanced,
      lastVisible: advancedLast,
    },
  ];

  // ================== Audio call Instructions and initiating ============ //
  const [instructions, setInstructions] = useState(false);
  const [isConnecting, setConnecting] = useState(false);
  const [callDetails, setCallDetails] = useState(null);
  const [toastDetails, setToastDetails] = useState(null);
  const handleCall = async (student) => {
    const senderDoc = await getDoc(doc(db, "bristu_talk_users", userId));
    const senderData = senderDoc.data();
    if (senderData.availability !== "online") {
      setToastDetails({
        type: "error",
        message: "Change your status to online to make a call",
      });
      return;
    }
    setCallDetails({ reciever: student, sender: senderData });
    setInstructions(true);
  };
  const [topic, setTopic] = useState(null);
  const handleResult = (result) => {
    setInstructions(false);
    if (result && result.status) {
      setTopic(result.topic);
      setConnecting(true);
    }
  };
  const onConnectionError = () => {
    setConnecting(false);
  };
  const [inCall, setInCall] = useState(false);

  const handleOnCall = (value) => {
    setInCall(value);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  // ===================== Audio Call Plan ==================== //
  const handlePlanResult = (result) => {
    setPurchaseLoading(true);
    const functions = getFunctions(app, "asia-south1");
    const payload = {
      platform: "web",
      plan_id: result,
      purchase_type: "talk",
    };
    const paymentRequest = httpsCallable(functions, "orderIdTalk");
    paymentRequest({
      img: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVf5kvPJI5bjBGVG5PiiueDXduVAV5p2kMcA&s",
      ...payload,
    }).then((result) => {
      const order = result.data;
      if (order && order !== 404) {
        const options = {
          key: process.env.REACT_APP_RAZOR_KEY,
          amount: order.amount,
          currency: order.currency,
          name: "Jaffer's English",
          description: "Talk Plan",
          image:
            "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVf5kvPJI5bjBGVG5PiiueDXduVAV5p2kMcA&s",
          order_id: order.id,
          method: {
            netbanking: true,
            card: true,
            upi: false,
            wallet: true,
          },
          handler: function (response) {
            openPaymentSuccess({
              status: "success",
              orderId: order.id,
              paymentId: response.razorpay_payment_id,
              courseTitle: "Talk Plan",
              amount: order.amount / 100,
            });
          },
          prefill: {
            name: user.name,
            email: user.email,
            contact: user.mobile,
          },
          notes: {
            address: "Razorpay Corporate Office",
          },
          theme: {
            color: "#3399cc",
          },
          modal: {
            ondismiss: function () {},
          },
        };

        try {
          const rzp1 = new window.Razorpay(options);
          rzp1.open();
          setPurchaseLoading(false);
          rzp1.on("payment.failed", function (response) {
            openPaymentSuccess({
              status: "failed",
              orderId: order.id,
              paymentId: response.razorpay_payment_id,
              courseTitle: "Talk Plan",
              amount: order.amount / 100,
              message: response.error.code,
            });
          });
        } catch (error) {
          setPurchaseLoading(false);
          openPaymentSuccess({
            status: "failed",
            orderId: order.id || "",
            paymentId: "",
            courseTitle: "Talk Plan",
            amount: order?.amount / 100 || "NA",
            message: error.message,
          });
        }
      }
    });
  };

  const [selectedLevel, setSelectedLevel] = useState("Beginner");

  const handleLevel = async (level) => {
    setSelectedLevel(level);
    await updateDoc(doc(db, "bristu_talk_users", getAuth().currentUser.uid), {
      level: level,
    });
  };

  return (
    <div className="px-8 py-8 min-h-screen">
      <Toast
        type={toastDetails?.type}
        message={toastDetails?.message}
        onComplete={() => setToastDetails(null)}
      />
      <LoadingTwo toggle={purchaseLoading} />
      {instructions && (
        <Instruction instructions={instructions} onResult={handleResult} />
      )}
      {isConnecting ? (
        <Callui
          status={isConnecting}
          topic={topic}
          callDetails={callDetails}
          onError={onConnectionError}
        />
      ) : (
        <>
          <IncomingCall status={isAvailable} onCall={handleOnCall} />
          {inCall === false ? (
            <>
              <div className="flex flex-wrap sm:flex-nowrap justify-between items-center bg-gradient-to-r from-gray-900 to-black p-4 rounded-2xl border border-gray-800 shadow-2xl">
                <h1 className="text-2xl hidden md:block px-3 text-white font-bold tracking-tight">
                  Bristu Talk
                </h1>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 p-3 sm:p-4 bg-gray-800 rounded-xl border border-gray-700 w-full sm:w-auto shadow-inner">
                  {/* Minutes Section */}
                  <div className="flex flex-row items-center gap-4">
                    <div className="text-left min-w-[140px]">
                      <p className="text-xs font-semibold text-emerald-400/80 mb-1 tracking-wider">
                        {user?.expiry_date
                          ? `Expire on ${new Date(
                              user.expiry_date.seconds * 1000
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}`
                          : "Trial"}
                      </p>
                      <h3 className="text-2xl font-bold text-emerald-400">
                        {(usageQuata / 60).toFixed(1)}
                        <span className="text-xs font-medium text-emerald-400/70 ml-1">
                          {payment === "trial"
                            ? "trial minutes"
                            : "daily minutes left"}
                        </span>
                      </h3>
                    </div>
                    {payment === "trial" && (
                      <button
                        onClick={() => setIsModalOpen(true)}
                        className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-medium rounded-lg 
           transition-all duration-200 hover:scale-[1.02] active:scale-95
           flex items-center gap-2 border border-emerald-500/30 shadow-md"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Add Minutes
                      </button>
                    )}
                  </div>

                  <div className="hidden sm:block h-auto w-px bg-gray-700 mx-1" />

                  <div className="flex flex-col">
                    <p className="text-xs font-semibold text-gray-200 mb-1 tracking-wider">
                      SKILL LEVEL
                    </p>
                    <div className="flex bg-gray-700 rounded-lg p-1 border border-gray-600">
                      {["Beginner", "Intermediate", "Advanced"].map((level) => (
                        <button
                          key={level}
                          onClick={() => handleLevel(level)}
                          className={`px-3 py-1 text-sm rounded-md transition-all duration-200 ${
                            selectedLevel === level
                              ? "bg-emerald-600/80 text-white shadow-sm"
                              : "text-gray-300 hover:text-white hover:bg-gray-600/50"
                          }`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="hidden sm:block h-auto w-px bg-gray-700 mx-1" />

                  <div className="flex flex-col justify-center">
                    <p className="text-xs font-semibold text-gray-400 mb-1 tracking-wider">
                      STATUS
                    </p>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAvailable}
                          onChange={updateStatus}
                          className="sr-only peer"
                        />
                        <div className="w-20 h-8 bg-gray-700 rounded-full peer peer-checked:bg-green-500/80 transition-colors duration-300 relative shadow-inner">
                          <span
                            className={`text-[10px] font-bold text-white absolute left-2.5 top-1/2 -translate-y-1/2 transition-opacity ${
                              isAvailable ? "opacity-100" : "opacity-0"
                            }`}
                          >
                            ONLINE
                          </span>
                          <span
                            className={`text-[10px] font-bold text-gray-300 absolute right-2.5 top-1/2 -translate-y-1/2 transition-opacity ${
                              isAvailable ? "opacity-0" : "opacity-100"
                            }`}
                          >
                            OFFLINE
                          </span>
                          <div
                            className={`absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                              isAvailable ? "translate-x-11" : "translate-x-0"
                            }`}
                          ></div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <Plan
                status={isModalOpen}
                onResult={handlePlanResult}
                onClose={() => setIsModalOpen(false)}
              />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                {levelCategories.map((category) => (
                  <div
                    key={category.title}
                    className="bg-black h-[650px] rounded-xl p-6 border border-gray-800"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-bold text-gray-100">
                        {category.title}
                      </h2>
                      <span
                        className={`${category.color} px-4 py-1.5 rounded-full text-sm font-medium`}
                      >
                        {category.title === "Beginner"
                          ? beginnerCount
                          : category.title === "Intermediate"
                          ? intermediateCount
                          : advancedCount}{" "}
                        Students
                      </span>
                    </div>
                    <div className="space-y-5 h-[550px] overflow-y-auto">
                      {category.students.length > 0 &&
                        category.students
                          .sort((a, b) =>
                            a.availability === "online"
                              ? -1
                              : b.availability === "online"
                              ? 1
                              : 0
                          )
                          .map((student) => {
                            const isDisabled =
                              student.availability !== "online";
                            return (
                              <div
                                key={student.id}
                                className="group flex items-center justify-between p-3 hover:bg-gray-700 rounded-xl transition-all duration-100 ease-out border border-transparent hover:border-gray-200 "
                              >
                                <div className="flex items-center space-x-4">
                                  <div className="relative">
                                    <div className="w-14 h-14 bg-yellow-600 rounded-full flex items-center justify-center text-white font-medium shadow-sm">
                                      {student.name
                                        .split(" ")
                                        .map((word) =>
                                          word.charAt(0).toUpperCase()
                                        )
                                        .join("")}
                                      <div
                                        className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-[1.5px] border-white ${
                                          statusColors[student.availability]
                                        }`}
                                        style={
                                          student.availability === "online"
                                            ? {
                                                animation: `
                                              orbit 4s infinite ease-in-out,
                                              pulse 2.5s infinite cubic-bezier(0.68, -0.55, 0.27, 1.55),
                                              glow 3s infinite ease-out
                                            `,
                                                boxShadow: `
                                              0 0 5px 2px rgba(74, 222, 128, 0.5),
                                              inset 0 0 10px rgba(255, 255, 255, 0.8)
                                            `,
                                              }
                                            : {}
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-sm text-gray-100">
                                      {student.name}
                                    </h3>
                                    <p className="text-sm text-gray-300">
                                      {category.title} Level
                                    </p>
                                  </div>
                                </div>
                                {isDisabled ? (
                                  <button
                                    disabled
                                    className="bg-gray-300 text-gray-900 px-4 py-2.5 rounded-xl flex items-center gap-2 cursor-not-allowed"
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                      />
                                    </svg>
                                    Call
                                  </button>
                                ) : (
                                  <div
                                    onClick={() => handleCall(student)}
                                    className="bg-emerald-700 hover:bg-emerald-900 text-white px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-200 hover:scale-[1.02] active:scale-95 cursor-pointer"
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                      />
                                    </svg>
                                    Call
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      {category.lastVisible && (
                        <div className="flex justify-center mt-8">
                          <button
                            onClick={category.loadMore}
                            disabled={category.loading}
                            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-all duration-200 shadow-md disabled:opacity-50"
                          >
                            {category.loading ? "Loading..." : "Load More"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}